import { useCallback, useEffect, useState } from 'react';

import {
  IBudgetServiceResponse,
  fetchBudgetFailureModes,
  fetchBudgetServices,
} from '../../../../api/fetchBudget';
import BudgetSelectCustom from './BudgetSelectCustom';
import { IVehicleData } from '../types';
import { Select } from '@rio-cloud/rio-uikit';
import { ITruckerPayPlanData } from '../../../../models/truckerPayPlan';
import SearchAutocomplete from '../../../form/searchAutocomplete';

interface IBudgetServiceSelectsProps {
  serviceId: string;
  failureModeId?: string;
  vehicleData?: IVehicleData;
  serviceType: string;
  truckerPayPlan?: ITruckerPayPlanData;
  onChangeItem: (itemId: string, serviceData?: IBudgetServiceResponse) => void;
  onChangeModoFalha: (modoFalhaId: string) => void;
  onChangeServiceType: (serviceType: string) => void;
}

const BudgetServiceSelects = (props: IBudgetServiceSelectsProps) => {
  const [serviceType, setServiceType] = useState<string>(props.serviceType);
  const [serviceId, setServiceId] = useState<string>(props.serviceId);
  const [failureModeId, setFailureModeId] = useState<string | undefined>(props.failureModeId);

  const loadServiceData = async (searchTerm: string): Promise<{ id: string; label: string; serviceData?: IBudgetServiceResponse }[]> => {
    const hasSearchTerm = searchTerm && searchTerm.trim() !== '';
    const {chassis, odometer, hourmeter} = props.vehicleData || {};
    const services = await fetchBudgetServices(hasSearchTerm ? searchTerm : undefined, chassis, odometer, hourmeter, 20);

    const additionalServices = [
      {
        id: 'CORRETIVA',
        label: 'Corretiva',
      },
      {
        id: 'DESGASTE',
        label: 'Desgaste',
      },
    ];

    const searchTermLower = searchTerm.toLowerCase();
    const filteredAdditionalServices = additionalServices.filter((additionalService) => 
      (!hasSearchTerm || additionalService.label.toLowerCase().includes(searchTermLower)) 
    );

    if (filteredAdditionalServices.length > 0) {
      const servicesToAdd = filteredAdditionalServices.filter(
        (additionalService) => !services.some((s) => s.id === additionalService.id)
      );
      if (servicesToAdd.length > 0) {
        return [...servicesToAdd, ...services].slice(0, 20);
      }
    }

    return services;
  };

  const loadFailureModeData = useCallback(
    async (searchTerm: string, limit: number): Promise<Array<{ id: string; label: string }>> => {
      const hasSearchTerm = searchTerm && searchTerm.trim() !== '';
      const failureModes = await fetchBudgetFailureModes(
        undefined,
        hasSearchTerm ? searchTerm : undefined,
        limit,
      );
      return failureModes;
    },
    [],
  );

  useEffect(() => {
    setServiceId(props.serviceId);
    setServiceType(props.serviceType || 'PREVENTIVE');
  }, [props.serviceId, props.serviceType]);

  useEffect(() => {
    if (props.serviceId === 'CORRETIVA') {
      setFailureModeId(props.failureModeId);
    } else {
      setFailureModeId(undefined);
    }
  }, [props.failureModeId, props.serviceId]);

  const handleServiceChange = (
    option: { id: string; label: string; serviceData?: IBudgetServiceResponse } | undefined,
  ) => {
    const newServiceId = option?.id || '';
    setServiceId(newServiceId);

    if (newServiceId !== 'CORRETIVA') {
      setFailureModeId(undefined);
      props.onChangeModoFalha('');
    }
    props.onChangeItem(newServiceId, option?.serviceData);
  };

  const handleFailureModeChange = (option: { id: string; label: string } | undefined) => {
    const newFailureModeId = option?.id || '';
    setFailureModeId(newFailureModeId);
    props.onChangeModoFalha(newFailureModeId);
  };

  const showFailureMode = serviceId === 'CORRETIVA';

  return (
    <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full">
      <div className="w-full sm:w-[250px]">
        <div className="font-bold mb-2 text-size-12 text-gray-500 whitespace-nowrap">Tipo de serviço</div>
        <Select
          options={[
            { id: 'PREVENTIVE', label: 'Revisão' },
            props.truckerPayPlan ? { id: 'CORRECTIVE', label: 'Corretiva' } : null,
            { id: 'OTHER', label: 'Demais serviços' },
          ].filter(Boolean) as Array<{ id: string; label: string }>}
          placeholder="Tipo de serviço"
          value={[serviceType]}
          onChange={(option) => { 
            props.onChangeServiceType(option?.id || ''); 
            // Clear the selected service when changing the service type
            handleServiceChange(undefined); 
          }}
        />
      </div>

      {serviceType === 'PREVENTIVE' && <div className="w-full sm:w-[250px]">
        <div className="font-bold mb-2 text-size-12 text-gray-500 whitespace-nowrap">Pacote de serviços *</div>
        <SearchAutocomplete 
          id="service-select"
          placeholder="Selecione o pacote de serviços"
          fetchOptions={loadServiceData}
          optionLabel={{ label: 'Nome' }}
          optionRenderer={({ label }) => <div>{label}</div>}
          onSelectedOption={handleServiceChange}
          overlay={true}
          debounceDelay={300}
          defaultValue={props.serviceId ? { id: props.serviceId, label: props.serviceId } : undefined}
          valueRenderer={(option) => option?.label || 'Selecione o pacote de serviços'}
          onClear={() => handleServiceChange(undefined)}
        />
      </div>}

      {props.serviceType === 'CORRECTIVE'  && <div className="w-full sm:w-[250px]">
        <div className="font-bold mb-2 text-size-12 text-gray-500 whitespace-nowrap">Pacote de serviços *</div>
        <Select
          options={(props.truckerPayPlan?.products
            .map(product => ({ id: product.description, label: product.description })) || []) as Array<{ id: string; label: string }>
          }
          placeholder="Pacote de serviços"
          value={[serviceId]}
          onChange={(option) => {
            const service = props.truckerPayPlan?.products.find(product => product.description === option?.id);
            const value = {
              id: option?.id || '',
              label: service?.description || '',
              serviceData: {
                id: service?.ref || '',
                name: service?.description || '',
                code: service?.ref || '',
                item: service?.ref || '',
                ref: service?.ref || '',
                value: null,
                serviceItems: service?.parts?.map(part => ({
                  item: part.id || part.sku || '',
                  description: part.name,
                  quantity: part.quantity,
                  value: part.unitPrice || null,
                  discount: null,
                  subtotal: part.totalPrice || null,
                  isCovered: service.isCovered,
                  notCoveredReason: service.notCoveredReason,
                })) || [],
              }
            };
            handleServiceChange(option ? value : undefined);
          }}
        />
      </div>}
      
      {showFailureMode && (
        <div className="w-full sm:w-[250px]">
          <BudgetSelectCustom
            value={failureModeId ? [failureModeId] : []}
            onChange={handleFailureModeChange}
            loadData={loadFailureModeData}
            label="Modo de falha"
            openOnFocus={false}
            initialLimit={10}
            inputBorderColor="#14b8a6"
            inputBackgroundColor="#ffffff"
            dropdownBackgroundColor="#ffffff"
            highlightedOptionColor="#3b82f6"
            selectedOptionColor="#e5e7eb"
          />
        </div>
      )}
    </div>
  );
};

export default BudgetServiceSelects;
